import axios from 'axios';
import _camelCase from '@gcc/configurator/dist/utils/camelCase';
import { camelCase } from 'lodash';
import { errorInteractions } from '../../analytics';
import { designsTransformer, cabinetdesignsTransformer, documentTransformerByDesignType } from './utils';
import logNewRelicCustom, { logNewRelicJSError } from '../../utils/logNewRelicCustom';
import { NEW_RELIC_EVENTS } from '../../constants';
import { isMockEnabledFromUrl } from '../configurator/mockService';
// eslint-disable-next-line import/no-extraneous-dependencies
import MOCK_BUNDLE_DESIGNS from '../../../mock-data/bundle-designs/index';

import { CABINET_SKU, isSpacerSku } from '../../components/Collections/cabinetConstants';

const logError = async (newRelicEvent, error, apiUrl, payLoad) => {
  let errorId = camelCase(newRelicEvent);
  const errorPayload = {};
  errorPayload[errorId] = {
    apiUrl,
    payLoad,
    message: error.message,
    stack: error.stack
  };
  logNewRelicJSError(new Error(JSON.stringify(errorPayload)));
  logNewRelicCustom(newRelicEvent, {
    endpoint: apiUrl,
    errorMessage: error.message,
    status: error?.response?.status,
  });
};

export const createDesign = async (host, productTypeId, modularDesignType) => {
  const errorMsg = 'Create Design: Request failed';
  const cacheBustParam = `t=${Date.now()}`;
  const apiUrl = `${host}/api/modulardesigner/design/create/${productTypeId}/${modularDesignType}?${cacheBustParam}`;
  try {
    const response = await axios.get(apiUrl);
    if (response?.status === 200) {
      const { data: { content } } = response;
      return content;
    }
    throw new Error(errorMsg);
  } catch (err) {
    const errorObject = {
      type: 'catastrophic',
      displayType: 'inline',
      pageSection: 'Create Design',
      message: errorMsg
    };
    const trackErrorInteractions = { errorObject, primaryCategory: 'error', eventName: 'post page error' };
    errorInteractions(trackErrorInteractions);

    logError(NEW_RELIC_EVENTS.CREATE_DESIGN_API_ERROR, err, apiUrl, null);
    throw new Error(errorMsg);
  }
};

export const tryGetDesign = async (host, designId) => {
  const errorMsg = 'Get Design: Request Failed';

  const randomParam = `t=${Date.now()}`;
  const url = `${host}/api/modulardesigner/design/${designId}?${randomParam}`;
  try {
    const response = await axios.get(url);
    if (response?.status === 200) {
      const { data: { content } } = response;
      const _parsed = await _camelCase(JSON.parse(content));

      return _parsed;
    }
    throw new Error(errorMsg);
  } catch (err) {
    const errorObject = {
      type: 'catastrophic',
      displayType: 'inline',
      pageSection: 'Get Design',
      message: errorMsg,
    };
    const trackErrorInteractions = { errorObject, primaryCategory: 'error', eventName: 'post page error' };
    errorInteractions(trackErrorInteractions);
    return null;
  }
};

export const saveDesign = async (host, productTypeId, modularDesignType, designData) => {
  const apiUrl = `${host}/api/modulardesigner/design/save/${productTypeId}/${modularDesignType}`;

  const errorMsg = 'Save Design: Request Failed';
  try {
    const response = await axios.post(
      apiUrl,
      designData
    );
    if (response?.status === 200) {
      return response;
    }
    throw new Error(errorMsg);
  } catch (err) {
    const errorObject = {
      type: 'catastrophic',
      displayType: 'inline',
      pageSection: 'Save Design',
      message: errorMsg,
    };
    const trackErrorInteractions = { errorObject, primaryCategory: 'error', eventName: 'post page error' };
    errorInteractions(trackErrorInteractions);

    logError(NEW_RELIC_EVENTS.SAVE_DESIGN_API_ERROR, err, apiUrl);
    throw new Error(errorMsg);
  }
};

// cabinets need a scale factor applied to position to convert Number inches to int inches x 100.
export const saveCabinetDesign = async (host, designData) => {
  const designToSave = {
    ...designData,
    Matrix: {
      ...designData.Matrix,
      Rows: Math.round(designData.Matrix.Rows * 100) ?? 0,
      Columns: Math.round(designData.Matrix.Columns * 100) ?? 0,
    },
    Products: (designData.Products ?? []).map((product) => ({
      Sku: product.Sku,
      Precedence: product.Precedence,
      Origin: {
        Row: Math.round(product.Origin.Row * 100),
        Column: Math.round(product.Origin.Column * 100),
        Height: Math.round(product.Origin.Height * 100),
        Width: Math.round(product.Origin.Width * 100),
      },
      // Use product's own ChoiceId for spacers (BASE_SPACER or WALL_SPACER), otherwise use design style
      ChoiceId: isSpacerSku(product.Sku) ? product.ChoiceId : designData.DesignStyleChoiceid,
      Name: product.Name
    })),
  };

  return saveDesign(host, 15, 'Cabinet', designToSave);
};

export const tryGetMultipleDesigns = async (host, ids) => {
  const errorMsg = 'Get Bundle Designs: Request Failed';
  try {
    // In mock mode, serve from the local mock registry instead of hitting the API.
    if (isMockEnabledFromUrl()) {
      const idList = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
      const mockItems = idList.flatMap((id) => MOCK_BUNDLE_DESIGNS[String(id).toLowerCase()] || []);
      if (mockItems.length > 0) {
        return designsTransformer(mockItems);
      }
    }

    const response = await axios({
      method: 'POST',
      url: `${host}/api/modulardesigner/design/bundlelist`,
      data: JSON.stringify(ids),
      headers: { 'Content-Type': 'application/json;charset=UTF-8' }
    });

    if (response?.data?.status === 200) {
      const parsedContent = JSON.parse(response?.data?.content);
      const transformedContent = designsTransformer(parsedContent);
      return transformedContent;
    }

    throw new Error(errorMsg);
  } catch (err) {
    const errorObject = {
      type: 'catastrophic',
      displayType: 'inline',
      pageSection: 'Get Multiple Designs - Bundles',
      message: errorMsg,
    };

    const trackErrorInteractions = { errorObject, primaryCategory: 'error', eventName: 'post page error' };
    errorInteractions(trackErrorInteractions);
    throw new Error(errorMsg);
  }
};

export const tryGetDesigns = async (host, ids, withConfiguratorBundleData = false) => {
  const errorMsg = 'Get Bundle Designs: Request Failed';
  const url = `${host}/api/modulardesigner/design/detaillist`;
  try {
    const response = await axios({
      method: 'POST',
      url,
      data: JSON.stringify(ids),
      headers: { 'Content-Type': 'application/json;charset=UTF-8' }
    });

    if (response?.status === 200) {
      const transformedContent = documentTransformerByDesignType(response?.data, withConfiguratorBundleData);
      return transformedContent;
    }

    throw new Error(errorMsg);
  } catch (err) {
    const errorObject = {
      type: 'catastrophic',
      displayType: 'inline',
      pageSection: 'Get Multiple Designs - Bundles',
      message: errorMsg,
    };

    const trackErrorInteractions = { errorObject, primaryCategory: 'error', eventName: 'post page error' };
    errorInteractions(trackErrorInteractions);
    logError(NEW_RELIC_EVENTS.LOAD_DESIGNS_API_ERROR, err, url, ids);
    throw new Error(errorMsg);
  }
};

export const tryGetMultipleCabinetDesigns = async (host, ids) => {
  const errorMsg = 'Get Bundle Designs: Request Failed';
  try {
    const response = await axios({
      method: 'POST',
      url: `${host}/api/modulardesigner/design/detaillist`,
      data: JSON.stringify(ids),
      headers: { 'Content-Type': 'application/json;charset=UTF-8' }
    });

    if (response?.status === 200) {
      const parsedContent = response?.data;
      const transformedContent = cabinetdesignsTransformer(parsedContent);
      return transformedContent;
    }

    throw new Error(errorMsg);
  } catch (err) {
    const errorObject = {
      type: 'catastrophic',
      displayType: 'inline',
      pageSection: 'Get Multiple Designs - Cabinet Designs',
      message: errorMsg,
    };

    const trackErrorInteractions = { errorObject, primaryCategory: 'error', eventName: 'post page error' };
    errorInteractions(trackErrorInteractions);
    throw new Error(errorMsg);
  }
};

export const tryGetCollections = async (host) => {
  const errorMsg = 'Get Collections: Request Failed';
  const apiUrl = `${host}/api/modulardesigner/cache/collections`;

  try {
    const response = await axios.get(apiUrl);

    if (response?.status === 200) {
      return { products: response?.data?.products || [],
        primaryFilters: response?.data?.primaryFilters || [] };
    }
    throw new Error(errorMsg);
  } catch (err) {

    const errorObject = {
      type: 'catastrophic',
      displayType: 'inline',
      pageSection: 'Get Collections',
      message: errorMsg,
    };
    const trackErrorInteractions = { errorObject, primaryCategory: 'error', eventName: 'post page error' };
    errorInteractions(trackErrorInteractions);
    return null;
  }
};

/**
 * Delete a design by ID via backend API
 * @param {string} host - API host
 * @param {string|number} designId - Design ID to delete
 * @returns {Promise<boolean>} true if deleted, false otherwise
 */
export async function deleteDesignById(host, designId) {
  const url = `${host}/api/modulardesigner/design/delete/${designId}`;
  try {
    const response = await axios.get(url);
    return response.status === 200;
  } catch (err) {
    // Optionally log error
    return false;
  }
}

export const createDesignWithProject = async (
  host, productTypeId, modularDesignType, projectId = null, userId = null
) => {
  const errorMsg = 'Create Design: Request failed';
  const cacheBustParam = `t=${Date.now()}`;

  let url = `${host}/api/modulardesigner/design/create/v2/${productTypeId}/${modularDesignType}`;

  try {
    const params = [cacheBustParam];

    if (projectId) {
      params.unshift(`projectId=${projectId}`);
    }

    if (userId) {
      params.push(`userId=${encodeURIComponent(userId)}`);
    }

    url += `?${params.join('&')}`;

    const response = await axios.get(url);
    if (response?.status === 200) {
      const { data: { content } } = response;

      // v2 endpoint returns content as JSON string: { content: "{\"designId\":\"...\",\"projectId\":\"...\"}" }
      try {
        const parsedContent = JSON.parse(content);
        if (parsedContent.designId && parsedContent.projectId) {
          return {
            designId: parsedContent.designId,
            projectId: parsedContent.projectId
          };
        }
      } catch (parseError) {
        // eslint-disable-next-line no-console
        console.error('Failed to parse v2 create endpoint response content:', parseError);
      }

      throw new Error('Invalid response format from v2 create endpoint');

    }
    throw new Error(errorMsg);
  } catch (err) {
    const errorObject = {
      type: 'catastrophic',
      displayType: 'inline',
      pageSection: 'Create Design',
      message: errorMsg
    };
    const trackErrorInteractions = { errorObject, primaryCategory: 'error', eventName: 'post page error' };
    errorInteractions(trackErrorInteractions);
    logError(NEW_RELIC_EVENTS.CREATE_DESIGN_API_ERROR, err, url, null);
    throw new Error(errorMsg);
  }
};

// Collection Matching API
export {
  fetchMatchingCollections,
  validateCollectionMapping,
  sortCollectionsByMatchCount
} from './collectionMatching';
